import type { BobState } from "../types";
import { STAGE_LABELS } from "../lib/engine/pipeline";

interface Props {
  state: BobState;
  toast: (msg: string) => void;
}

const ROLE_DESCRIPTIONS: Record<string, { full: string; compact: string }> = {
  bob: {
    full: "Orchestrator — intake routing, handoff, sovereign runtime policy.",
    compact: "Orchestrator",
  },
  felix: {
    full: "Code builder — implementation, scaffolding, component assembly.",
    compact: "Builder",
  },
  lisa: {
    full: "Architect — decomposition, artifact plans, packaging strategy.",
    compact: "Architect",
  },
  executor: {
    full: "Executor — test/repair loops, merge salvage, export runs.",
    compact: "Executor",
  },
};

export function AgentBoardPanel({ state, toast }: Props) {
  const compact = state.presentationMode;

  function pingAgent(name: string) {
    toast(compact ? `Pinged ${name}` : `${name} acknowledged — standing by on local channel`);
  }

  return (
    <section className="surface glass-panel">
      <h2>{compact ? "Agents" : "Agent Board"}</h2>
      <p className="lead">
        {compact
          ? "Role status and utilization."
          : "Local agent cards synced with pipeline assignments — no external API calls."}
      </p>

      <div className="agent-grid">
        {state.agents.map((agent) => {
          const directive = state.directives.find((d) => d.id === agent.currentDirectiveId);
          const desc = ROLE_DESCRIPTIONS[agent.role];
          return (
            <article key={agent.id} className={`agent-card status-${agent.status}`}>
              <div className="agent-card-head">
                <h3>{agent.displayName}</h3>
                <span className={`chip ${agent.status === "active" ? "ok" : agent.status === "blocked" ? "locked" : ""}`}>
                  {agent.status}
                </span>
              </div>
              <p className="agent-role">{compact ? desc?.compact : desc?.full}</p>
              <p className="agent-task">{agent.taskSummary}</p>
              {directive && (
                <p className="agent-directive">
                  {compact
                    ? `${directive.name} · ${STAGE_LABELS[directive.stage]}`
                    : `Directive: ${directive.name} — ${STAGE_LABELS[directive.stage]} (${directive.progress}%)`}
                </p>
              )}
              <div className="progress-bar slim">
                <div className="progress-fill" style={{ width: `${agent.utilization}%` }} />
              </div>
              <span className="chip">{agent.utilization}% util</span>
              <div className="btn-row">
                <button type="button" className="btn secondary" onClick={() => pingAgent(agent.displayName)}>
                  Ping
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
